import LeftOutlined from '@ant-design/icons/lib/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/lib/icons/RightOutlined';
import { ConfigProvider, Divider, Flex, Form } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchOptions, putQuestionnaire } from '@/apis';
import companyLogo from '@/assets/images/company-logo.png';
import unionLogo from '@/assets/images/union-logo.png';
import Logo from '@/components/Logo';
import ProgressBar from '@/components/ProgressBar';
import {
  HamburgerMenu,
  RadioGroup,
  Select,
  TextArea,
} from '@/components/Questionniare';
import { AnswerType, MessageType, Option, QuestionModule } from '@/enums';
import {
  useApiHandler,
  useAppFeedback,
  useBreakpoint,
  useDebounce,
} from '@/hooks';
import { OptionType, QuestionWithAnswer } from '@/types';
import { formatQuestionNumber } from '@/utils/common';

import * as Style from './BusinessModule.style';

interface BusinessModuleProps {
  questions: QuestionWithAnswer[];
  currentQuestionIndex: number;
  onCrossModuleNavigation: (direction: 'prev' | 'next') => Promise<void>;
  canChangeIndustry?: boolean;
}

const BusinessModule = ({
  questions,
  currentQuestionIndex: initialQuestionIndex,
  onCrossModuleNavigation,
  canChangeIndustry = true,
}: BusinessModuleProps) => {
  const [form] = Form.useForm();
  const [answersMap, setAnswersMap] = useState<Map<string, string>>(new Map());
  const [currentQuestionIndex, setCurrentQuestionIndex] =
    useState(initialQuestionIndex);
  const [currentQuestion, setCurrentQuestion] =
    useState<QuestionWithAnswer | null>(null);
  const [logosVisible, setLogosVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Breakpoint detection for responsive rendering
  const { isMobileOrTablet } = useBreakpoint();

  const { questionnaireId } = useParams<{ questionnaireId: string }>();

  const { handleApiCall } = useApiHandler();
  const { showMessage } = useAppFeedback();

  const saveAnswerOnly = async (
    questionId: string,
    answer: string,
    industryId?: string,
  ) => {
    try {
      await handleApiCall(
        () =>
          putQuestionnaire(
            {
              questionnaireId: questionnaireId!,
              questionId,
              answer,
              ...(industryId ? { industryId } : {}),
            },
            false,
          ),
        { showLoading: false },
      );
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleBack = async () => {
    if (isNavigating) return;
    setIsNavigating(true);

    try {
      if (currentQuestionIndex > 0) {
        const newIndex = currentQuestionIndex - 1;
        const newQuestion = questions[newIndex];

        if (newQuestion) {
          await handleApiCall(
            () =>
              putQuestionnaire(
                {
                  questionnaireId: questionnaireId!,
                  currentQuestionId: newQuestion.id,
                },
                false,
              ),
            { showLoading: false },
          );

          setCurrentQuestionIndex(newIndex);
          setCurrentQuestion(newQuestion);
        }
      } else {
        await onCrossModuleNavigation('prev');
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const handleNext = async () => {
    if (isNavigating) return;
    if (!currentQuestion) return;

    setIsNavigating(true);

    try {
      if (currentQuestionIndex < questions.length - 1) {
        const newIndex = currentQuestionIndex + 1;
        const newQuestion = questions[newIndex];

        if (!newQuestion) {
          setIsNavigating(false);
          return;
        }

        if (currentQuestion.answerType === AnswerType.Info) {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    questionId: currentQuestion.id,
                    answer: 'true',
                    currentQuestionId: newQuestion.id,
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to save info question answer:', error);
            return;
          }
        } else {
          try {
            await form.validateFields();
          } catch {
            return;
          }
          const currentAnswer = form.getFieldValue(currentQuestion.id);
          if (
            currentAnswer !== undefined &&
            currentAnswer !== null &&
            currentAnswer !== ''
          ) {
            try {
              const shouldPassIndustryId =
                currentQuestion.answerType === AnswerType.Select;

              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer.toString(),
                      currentQuestionId: newQuestion.id,
                      ...(shouldPassIndustryId
                        ? { industryId: currentAnswer.toString() }
                        : {}),
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error(
                'Failed to save answer and update position:',
                error,
              );
              return;
            }
          } else {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      currentQuestionId: newQuestion.id,
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to update position:', error);
              return;
            }
          }
        }

        setCurrentQuestionIndex(newIndex);
        setCurrentQuestion(newQuestion);
      } else {
        if (currentQuestion.answerType === AnswerType.Info) {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    questionId: currentQuestion.id,
                    answer: 'true',
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to save info question answer:', error);
            return;
          }
        } else {
          try {
            await form.validateFields();
          } catch {
            return;
          }
          const currentAnswer = form.getFieldValue(currentQuestion.id);
          if (
            currentAnswer !== undefined &&
            currentAnswer !== null &&
            currentAnswer !== ''
          ) {
            try {
              const shouldPassIndustryId =
                currentQuestion.answerType === AnswerType.Select;

              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer.toString(),
                      ...(shouldPassIndustryId
                        ? { industryId: currentAnswer.toString() }
                        : {}),
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to save answer:', error);
              return;
            }
          }
        }

        await onCrossModuleNavigation('next');
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const debouncedSaveAnswer = useDebounce(saveAnswerOnly, 300);

  const handleDisabledSelectClick = () => {
    if (!canChangeIndustry) {
      showMessage('已選擇產業，無法更改', MessageType.Warning);
    }
  };

  useEffect(() => {
    if (questions.length > 0) {
      const newAnswersMap = new Map<string, string>();
      const formValues: Record<string, string> = {};

      questions.forEach((q) => {
        if (q.answer) {
          formValues[q.id] = q.answer;
          newAnswersMap.set(q.id, q.answer);
        }
      });

      setAnswersMap(newAnswersMap);

      form.setFieldsValue(formValues);

      setCurrentQuestionIndex(initialQuestionIndex);
      setCurrentQuestion(questions[initialQuestionIndex] || null);
      setMounted(true);
    }
  }, [questions, initialQuestionIndex, form]);

  const [industriesOptions, setIndustriesOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    const init = async () => {
      const options = await handleApiCall(() => fetchOptions(Option.Industry), {
        context: '獲取行業選項',
      });
      if (options && options.length > 0) {
        setIndustriesOptions(options);
      }
    };

    init();
  }, []);

  // Mobile/Tablet Page - Conditional Rendering
  if (isMobileOrTablet) {
    return (
      currentQuestion &&
      mounted && (
        <Style.MobileContainer>
          <HamburgerMenu
            questions={questions}
            answersMap={answersMap}
            module={QuestionModule.Business}
            currentStep={
              currentQuestion.answerType !== AnswerType.Info
                ? questions
                    .slice(0, currentQuestionIndex + 1)
                    .filter((q) => q.answerType !== AnswerType.Info).length - 1
                : undefined
            }
            totalSteps={
              currentQuestion.answerType !== AnswerType.Info
                ? questions.filter((q) => q.answerType !== AnswerType.Info)
                    .length
                : undefined
            }
            onNavigate={async (index: number, questionId: string) => {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      currentQuestionId: questionId,
                    },
                    false,
                  ),
                { showLoading: false },
              );

              setCurrentQuestionIndex(index);
              const question = questions.find((q) => q.id === questionId);
              setCurrentQuestion(question || null);
            }}
          />

          <Style.MobileContentArea>
            {currentQuestion.answerType !== AnswerType.Info && (
              <>
                <Style.Title>業務範疇</Style.Title>
                <Divider size="small" />

                <Style.QuestionNumber>
                  {formatQuestionNumber(currentQuestion.order)}
                </Style.QuestionNumber>
              </>
            )}

            <Style.MobileTitle
              $info={currentQuestion.answerType === AnswerType.Info}
            >
              {currentQuestion.question}
            </Style.MobileTitle>

            <Style.MobileDescription>
              {currentQuestion.information}
            </Style.MobileDescription>

            {currentQuestion.answerType === AnswerType.Info && (
              <Style.MobileLogoArea justify="center" gap={48}>
                <Logo src={companyLogo} alt="Company Logo" height="40px" />
                <Logo src={unionLogo} alt="Union Logo" height="40px" />
              </Style.MobileLogoArea>
            )}

            <Form
              form={form}
              component={false}
              onValuesChange={(changedValues: Record<string, unknown>) => {
                Object.entries(changedValues).forEach(([fieldName, value]) => {
                  if (value != null && value !== '') {
                    const questionId = fieldName;
                    const valueStr = value.toString();
                    setAnswersMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set(questionId, valueStr);
                      return newMap;
                    });

                    const question = questions.find((q) => q.id === questionId);

                    if (question) {
                      const shouldPassIndustryId =
                        question.answerType === AnswerType.Select;

                      if (question.answerType === AnswerType.Textarea) {
                        debouncedSaveAnswer(
                          questionId,
                          valueStr,
                          shouldPassIndustryId ? valueStr : undefined,
                        );
                      } else if (question.answerType !== AnswerType.Info) {
                        saveAnswerOnly(
                          questionId,
                          valueStr,
                          shouldPassIndustryId ? valueStr : undefined,
                        );
                      }
                    }
                  }
                });
              }}
            >
              <Style.FormContainer>
                {currentQuestion.answerType === AnswerType.Radio && (
                  <Form.Item
                    name={currentQuestion.id}
                    rules={[
                      {
                        required: currentQuestion.isRequired,
                        message: '此為必填欄位',
                      },
                    ]}
                    className="radio-in-form-item"
                  >
                    <RadioGroup options={industriesOptions} />
                  </Form.Item>
                )}

                {currentQuestion.answerType === AnswerType.Select && (
                  <Form.Item
                    name={currentQuestion.id}
                    rules={[
                      {
                        required: currentQuestion.isRequired,
                        message: '此為必填欄位',
                      },
                    ]}
                    className="select-in-form-item"
                  >
                    <Select
                      options={industriesOptions}
                      disabled={!canChangeIndustry}
                      onOpenChange={(open) => setLogosVisible(!open)}
                      onClick={handleDisabledSelectClick}
                    />
                  </Form.Item>
                )}

                {currentQuestion.answerType === AnswerType.Textarea && (
                  <Form.Item
                    name={currentQuestion.id}
                    rules={[
                      {
                        required: currentQuestion.isRequired,
                        message: '此為必填欄位',
                      },
                    ]}
                    className="textarea-in-form-item"
                  >
                    <TextArea />
                  </Form.Item>
                )}
              </Style.FormContainer>
            </Form>
          </Style.MobileContentArea>

          <Style.MobileButtonArea>
            <Style.MobileBackButtonWrapper>
              <Style.BackButton
                type="text"
                onClick={isNavigating ? undefined : handleBack}
                style={{
                  visibility: currentQuestionIndex > 0 ? 'visible' : 'hidden',
                  position: 'absolute',
                  bottom: '17%',
                  left: '8%',
                }}
              >
                <LeftOutlined />
                <span>BACK</span>
              </Style.BackButton>
            </Style.MobileBackButtonWrapper>

            <Style.MobileNextButtonWrapper>
              <ConfigProvider wave={{ disabled: true }}>
                <Style.NextButton
                  onClick={isNavigating ? undefined : handleNext}
                  $gradient="linear-gradient(#586c64 0%, #8da67f 100%)"
                />
              </ConfigProvider>
              <Style.ButtonText>
                <span>NEXT</span>
                <RightOutlined />
              </Style.ButtonText>
            </Style.MobileNextButtonWrapper>
          </Style.MobileButtonArea>
        </Style.MobileContainer>
      )
    );
  }

  return (
    questions.length > 0 &&
    currentQuestion &&
    mounted && (
      <Style.QuestionnaireContainer>
        <HamburgerMenu
          questions={questions}
          answersMap={answersMap}
          module={QuestionModule.Business}
          onNavigate={async (index: number, questionId: string) => {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    currentQuestionId: questionId,
                  },
                  false,
                ),
              { showLoading: false },
            );

            setCurrentQuestionIndex(index);
            const question = questions.find((q) => q.id === questionId);
            setCurrentQuestion(question || null);
          }}
        />

        {currentQuestion.answerType !== AnswerType.Info && (
          <ProgressBar
            currentStep={
              questions
                .slice(0, currentQuestionIndex + 1)
                .filter((q) => q.answerType !== AnswerType.Info).length - 1
            }
            totalSteps={
              questions.filter((q) => q.answerType !== AnswerType.Info).length
            }
          />
        )}

        <Style.QuestionnaireWrapper vertical justify="space-between">
          <div
            style={{
              display: 'grid',
              gridTemplateRows: 'auto 1fr',
              rowGap: '24px',
              flex: 1,
            }}
          >
            <Style.BackButton
              type="text"
              onClick={isNavigating ? undefined : handleBack}
              style={{
                visibility: currentQuestionIndex > 0 ? 'visible' : 'hidden',
              }}
            >
              <LeftOutlined />
              <span>BACK</span>
            </Style.BackButton>

            {currentQuestion && mounted && (
              <>
                {currentQuestion.answerType === AnswerType.Info ? (
                  <Style.BeforeBusinessStageContainer>
                    <Style.InfoTitle>
                      {currentQuestion.question}
                    </Style.InfoTitle>
                    <Style.DescriptionWrapper>
                      <Style.Description>
                        {currentQuestion.information}
                      </Style.Description>
                    </Style.DescriptionWrapper>
                  </Style.BeforeBusinessStageContainer>
                ) : (
                  <Style.BusinessStageContainer>
                    <Flex vertical gap={16}>
                      <Style.Title>業務範疇</Style.Title>
                      <Flex gap="2rem">
                        <Style.QuestionNumber>
                          {formatQuestionNumber(currentQuestion.order)}
                        </Style.QuestionNumber>
                        <Flex vertical justify="center" gap={8}>
                          <Style.QuestionText>
                            {currentQuestion.question}
                          </Style.QuestionText>
                          {currentQuestion.information && (
                            <Style.QuestionInformation>
                              {currentQuestion.information}
                            </Style.QuestionInformation>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>

                    <Form
                      form={form}
                      component={false}
                      onValuesChange={(changedValues) => {
                        Object.entries(changedValues).forEach(
                          ([fieldName, value]) => {
                            if (value != null && value !== '') {
                              const questionId = fieldName;
                              const valueStr = value.toString();
                              setAnswersMap((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(questionId, valueStr);
                                return newMap;
                              });

                              const question = questions.find(
                                (q) => q.id === questionId,
                              );

                              if (question) {
                                const shouldPassIndustryId =
                                  question.answerType === AnswerType.Select;

                                if (
                                  question.answerType === AnswerType.Textarea
                                ) {
                                  debouncedSaveAnswer(
                                    questionId,
                                    valueStr,
                                    shouldPassIndustryId ? valueStr : undefined,
                                  );
                                } else if (
                                  question.answerType !== AnswerType.Info
                                ) {
                                  saveAnswerOnly(
                                    questionId,
                                    valueStr,
                                    shouldPassIndustryId ? valueStr : undefined,
                                  );
                                }
                              }
                            }
                          },
                        );
                      }}
                    >
                      <Style.FormContainer>
                        {currentQuestion.answerType === AnswerType.Radio && (
                          <Form.Item
                            name={currentQuestion.id}
                            rules={[
                              {
                                required: currentQuestion.isRequired,
                                message: '此為必填欄位',
                              },
                            ]}
                            className="radio-in-form-item"
                          >
                            <RadioGroup options={industriesOptions} />
                          </Form.Item>
                        )}

                        {currentQuestion.answerType === AnswerType.Select && (
                          <Form.Item
                            name={currentQuestion.id}
                            rules={[
                              {
                                required: currentQuestion.isRequired,
                                message: '此為必填欄位',
                              },
                            ]}
                            className="select-in-form-item"
                          >
                            <Select
                              options={industriesOptions}
                              disabled={!canChangeIndustry}
                              onOpenChange={(open) => setLogosVisible(!open)}
                              onClick={handleDisabledSelectClick}
                            />
                          </Form.Item>
                        )}

                        {currentQuestion.answerType === AnswerType.Textarea && (
                          <Form.Item
                            name={currentQuestion.id}
                            rules={[
                              {
                                required: currentQuestion.isRequired,
                                message: '此為必填欄位',
                              },
                            ]}
                            className="textarea-in-form-item"
                          >
                            <TextArea />
                          </Form.Item>
                        )}
                      </Style.FormContainer>
                    </Form>
                  </Style.BusinessStageContainer>
                )}
              </>
            )}
          </div>

          <Style.LogoContainer
            vertical
            align="start"
            gap={16}
            $show={currentQuestion?.answerType === AnswerType.Info}
            $logosVisible={logosVisible}
          >
            <Logo src={companyLogo} alt="Company Logo" />
            <Logo src={unionLogo} alt="Union Logo" />
          </Style.LogoContainer>
        </Style.QuestionnaireWrapper>

        <ConfigProvider wave={{ disabled: true }}>
          <Style.NextButton
            onClick={isNavigating ? undefined : handleNext}
            $gradient="linear-gradient(#586c64 0%, #8da67f 100%)"
          >
            <Style.ButtonTextWrapper>
              <Style.ButtonText>
                <span>NEXT</span>
                <RightOutlined />
              </Style.ButtonText>
            </Style.ButtonTextWrapper>
          </Style.NextButton>
        </ConfigProvider>
      </Style.QuestionnaireContainer>
    )
  );
};

export default BusinessModule;
